import React, { useState, ReactNode, SetStateAction, Dispatch } from 'react';

import { createContext, useContext } from 'use-context-selector';
import { PAGES } from '../Router/pages';
import { pageRoutes } from '../flows/General/router/pages';
import { useAnalytics } from '../hooks/useAnalytics';

export type RouterProps = {
  route: number;
  primaryRoute?: number;
  goTo: (route: number, state?: any, reset?: boolean) => void;
  primaryRouteNavigate?: (route: number) => void;
  goBack: () => void;
  currentStep: number;
  totalSteps: number;
  incrementStep: () => void;
  decreaseStep: () => void;
  resetRoutes: () => void;
  resetSteps: () => void;
  state?: any;
  setState?: Dispatch<SetStateAction<any>>;
  history: number[];
};

type RouterProviderProps = {
  children: ReactNode;
};

export const RouterContext = createContext({} as RouterProps);

export function RouterProvider({ children }: RouterProviderProps) {
  const [route, setRoute] = useState<number>(-1);
  const [primaryRoute, setPrimaryRoute] = useState<number>(0);
  const [history, setHistory] = useState<number[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [state, setState] = useState({});
  const totalSteps = 6;

  const { logEvent } = useAnalytics();

  const addRouteToHistory = (route: number) => {
    const lastRoute = history[history.length - 1];
    if (lastRoute !== route) setHistory([...history, route]);
  };

  const goTo = (routeNumber: number, state?: any, reset?: boolean) => {
    if (!state) setState({});
    else setState(state);
    setRoute(routeNumber);

    if (!reset) addRouteToHistory(routeNumber);

    if (route === PAGES.SERVICE_UNIT.id && routeNumber === PAGES.APPOINTMENT_CONFIRMATION.id) {
      // TEMPORARY_VACCINE_SCHEDULE
      // incrementStep();
    }
    incrementStep();

    logEvent({
      eventName: 'screen_view',
      eventParams: {
        firebase_screen: pageRoutes(routeNumber)?.title ?? ''
      }
    });
  };

  const primaryRouteNavigate = (routeNumber: number) => {
    setPrimaryRoute(routeNumber);
  };

  const goBack = () => {
    const previousPage = history[history.length - 2];

    if (previousPage || previousPage === 0) {
      const newHistory = history.filter((_, index) => {
        return index < history.length - 1;
      });
      setHistory([...newHistory]);
      setRoute(previousPage);

      if (
        route !== PAGES.PAYMENT_VIEW.id &&
        route !== PAGES.SERVICE_TYPE_PAGE.id &&
        route !== PAGES.SERVICE_UNIT.id &&
        previousPage !== PAGES.SERVICE_UNIT.id
      )
        decreaseStep();
      else if (previousPage === PAGES.PATIENT_FORM.id) {
        decreaseStep();
      }

      //  --
      if (route === PAGES.APPOINTMENT_CONFIRMATION.id && previousPage === PAGES.SERVICE_UNIT.id) {
        decreaseStep();
      }
    }
  };

  const incrementStep = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const decreaseStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const resetRoutes = () => {
    // TEMPORARY_VACCINE_SCHEDULE
    // setRoute(1);
    setRoute(-1);
    setHistory(_ => []);
    setCurrentStep(1);
  };

  const resetSteps = () => {
    setCurrentStep(1);
  };

  return (
    <RouterContext.Provider
      value={{
        route,
        goTo,
        goBack,
        currentStep,
        totalSteps,
        resetRoutes,
        incrementStep,
        resetSteps,
        decreaseStep,
        primaryRoute,
        primaryRouteNavigate,
        state,
        setState,
        history
      }}
    >
      {children}
    </RouterContext.Provider>
  );
}

export function useRouter() {
  return useContext(RouterContext);
}
